import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ChevronLeft, Download, Languages, HeartPulse, Phone, Smartphone, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { cacheEmergency, readEmergency } from "@/lib/offlineCache";

function Row({ label, value }) {
  return (
    <div className="py-3 border-b border-white/15 last:border-0">
      <p className="text-xs uppercase tracking-widest text-white/60">{label}</p>
      <p className="text-xl font-bold leading-tight mt-0.5">{value}</p>
    </div>
  );
}

export default function MedicalCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offlineAt, setOfflineAt] = useState(null);
  const [showLocal, setShowLocal] = useState(false);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api
      .get("/emergency")
      .then(({ data }) => {
        setData(data);
        setOfflineAt(null);
        cacheEmergency(data);
      })
      .catch(() => {
        const c = readEmergency();
        if (c) {
          setData(c.data);
          setOfflineAt(c.savedAt);
        }
      });
  }, []);

  const save = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#111111",
        filter: (n) => !(n?.dataset && n.dataset.noCapture === "true"),
      });
      const link = document.createElement("a");
      link.download = `pocketdoc-medical-id${showLocal ? "-" + data.language.toLowerCase() : ""}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved. Set it as your lock-screen wallpaper for instant access.");
    } catch (e) {
      console.error("save medical id failed", e);
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#1A1A1A] flex flex-col">
        <header className="px-4 py-4 flex items-center gap-3 text-white no-print">
          <button
            data-testid="medcard-back"
            aria-label="Go back"
            onClick={() => navigate("/emergency")}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl hover:bg-white/10"
          >
            <ChevronLeft size={26} />
          </button>
          <h1 className="font-head text-xl font-bold">Medical ID</h1>
          {data && (
            <button
              data-testid="medcard-translate"
              onClick={() => setShowLocal((s) => !s)}
              className="ml-auto inline-flex items-center gap-1 text-sm font-semibold bg-white/10 rounded-full px-3 py-2 min-h-[44px]"
            >
              <Languages size={16} />
              {showLocal ? "English" : data.language}
            </button>
          )}
        </header>

        {!data ? (
          <div className="px-5">
            <div className="h-80 bg-white/10 rounded-2xl animate-pulse" />
          </div>
        ) : (
          <div className="px-5 pb-10 flex-1 flex flex-col">
            {offlineAt && (
              <div data-testid="medcard-offline-banner" className="mb-3 flex items-center gap-2 text-xs text-[#EFC77A] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl px-3 py-2">
                <WifiOff size={14} className="shrink-0" />
                <span>Offline — showing your saved copy from {new Date(offlineAt).toLocaleDateString()}.</span>
              </div>
            )}
            <MedCardBody data={data} showLocal={showLocal} cardRef={cardRef} />

            <button
              data-testid="medcard-save-btn"
              onClick={save}
              disabled={saving}
              className="mt-5 w-full min-h-[56px] rounded-xl bg-[#EF9F27] hover:bg-[#EF9F27] text-white text-lg font-semibold flex items-center justify-center gap-2"
            >
              <Download size={20} /> {saving ? "Saving…" : "Save as image"}
            </button>

            <div className="mt-4 flex items-start gap-2 text-white/70 text-sm">
              <Smartphone size={18} className="shrink-0 mt-0.5" />
              <p>
                Save this card, then set it as your phone's <strong>lock-screen wallpaper</strong> so
                first responders can see it without unlocking your phone.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MedCardBody({ data, showLocal, cardRef }) {
  const ms = data.medical_summary;
  const sl = data.translations.summary_local;
  const useLocal = showLocal && !!sl;
  const labels = showLocal ? data.translations.labels : data.translations.english_labels;
  const none = labels.none_recorded;
  const list = (a) => (a && a.length ? a.join(", ") : none);
  const conditions = useLocal ? sl.conditions : ms.conditions;
  const medications = useLocal ? sl.medications : ms.medications;
  const allergies = useLocal ? sl.allergies : ms.allergies;

  return (
    <div ref={cardRef} className="bg-[#1A1A1A] rounded-2xl border border-white/15 overflow-hidden">
      <div className="bg-[#D32F2F] text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse size={22} />
          <span className="font-head font-bold text-lg">{labels.title}</span>
        </div>
        <a
          href={`tel:${data.emergency_number}`}
          data-no-capture="true"
          className="inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1 font-bold"
        >
          <Phone size={15} /> {data.emergency_number}
        </a>
      </div>
      <div className="text-white px-5 py-2">
        <Row label={labels.conditions} value={list(conditions)} />
        <Row label={labels.medications} value={list(medications)} />
        <Row label={labels.allergies} value={list(allergies)} />
        <Row
          label={labels.emergency_contact}
          value={
            ms.emergency_contact_name
              ? `${ms.emergency_contact_name} — ${ms.emergency_contact_phone}`
              : none
          }
        />
      </div>
      <p className="text-[11px] text-white/50 px-5 pb-4">
        PocketDocta · Not medical advice · Emergency: {data.emergency_number}
        {data.active_trip ? ` · ${data.active_trip.city}, ${data.active_trip.country}` : ""}
      </p>
    </div>
  );
}
